import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { chapterAPI } from '../services/api';

function ChapterProgressMatrix() {
  const { courseId, chapterId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      setError('Access denied. Admin only.');
      navigate('/courses');
      return;
    }
    fetchProgressMatrix();
  }, [chapterId, user, navigate]);

  const fetchProgressMatrix = async () => {
    try {
      setLoading(true);
      const response = await chapterAPI.getChapterProgressMatrix(chapterId);
      setData(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch progress matrix');
    } finally {
      setLoading(false);
    }
  };

  const getAssetTypeIcon = (assetType) => {
    const icons = {
      video: '🎥',
      document: '📄',
      quiz: '📝',
      flashcard: '🗂️',
      interview: '🎤',
      link: '🔗',
      note: '📋',
      transcript: '📃',
      markdown: '📝'
    };
    return icons[assetType] || '📄';
  };

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '20px' }}>
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#667eea', fontSize: '16px' }}>Loading progress matrix...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ paddingTop: '20px' }}>
        <div className="card">
          <div className="error">{error}</div>
          <button 
            onClick={() => navigate(`/courses/${courseId}/chapters/progress`)}
            className="btn btn-secondary"
          >
            ← Back to Chapters
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container" style={{ paddingTop: '20px' }}>
        <div className="card">
          <p>No data available</p>
          <button 
            onClick={() => navigate(`/courses/${courseId}/chapters/progress`)}
            className="btn btn-secondary"
          >
            ← Back to Chapters
          </button>
        </div>
      </div>
    );
  }

  const { chapter, assets, progressMatrix: unsortedProgressMatrix } = data;

  // Sort students alphabetically by name
  const progressMatrix = [...unsortedProgressMatrix].sort((a, b) => 
    a.studentName.localeCompare(b.studentName)
  );

  // Calculate overall statistics
  const totalCells = progressMatrix.length * assets.length;
  const completedCells = progressMatrix.reduce((sum, student) => 
    sum + student.assetCompletion.filter(ac => ac.completed).length, 0
  );
  const completionPercentage = totalCells > 0 ? Math.round((completedCells / totalCells) * 100) : 0;

  return (
    <div className="container" style={{ paddingTop: '20px', maxWidth: '100%', padding: '20px' }}>
      <div className="card" style={{ overflow: 'visible' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <button
            onClick={() => navigate(`/courses/${courseId}/chapters/progress`)}
            style={{
              padding: '6px 12px',
              background: '#f7fafc',
              color: '#667eea',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '16px'
            }}
          >
            ← Back to Chapters
          </button>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '16px', flexWrap: 'wrap' }}>
            <div>
              <h2 style={{ 
                color: '#2d3748', 
                fontSize: '24px', 
                fontWeight: '700',
                marginBottom: '8px'
              }}>
                {chapter.title}
              </h2>
              {chapter.description && (
                <p style={{ 
                  color: '#718096', 
                  fontSize: '14px',
                  marginBottom: '12px'
                }}>
                  {chapter.description}
                </p>
              )}
              <div style={{ 
                display: 'flex', 
                gap: '16px',
                flexWrap: 'wrap',
                marginTop: '8px'
              }}>
                <div style={{ 
                  padding: '8px 16px',
                  background: '#edf2f7',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#4a5568'
                }}>
                  <strong>{assets.length}</strong> {assets.length === 1 ? 'Asset' : 'Assets'}
                </div>
                <div style={{ 
                  padding: '8px 16px',
                  background: '#edf2f7',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#4a5568'
                }}>
                  <strong>{progressMatrix.length}</strong> {progressMatrix.length === 1 ? 'Student' : 'Students'}
                </div>
                <div style={{ 
                  padding: '8px 16px',
                  background: completionPercentage >= 80 ? '#c6f6d5' : completionPercentage >= 50 ? '#fef3c7' : '#fed7d7',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: completionPercentage >= 80 ? '#22543d' : completionPercentage >= 50 ? '#92400e' : '#742a2a',
                  fontWeight: '600'
                }}>
                  {completionPercentage}% Overall Completion
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Empty State */}
        {assets.length === 0 || progressMatrix.length === 0 ? (
          <div style={{
            padding: '48px 24px',
            textAlign: 'center',
            background: '#f7fafc',
            borderRadius: '12px',
            border: '2px dashed #cbd5e0'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              {assets.length === 0 ? '📄' : '👥'}
            </div>
            <p style={{ fontSize: '16px', color: '#718096', marginBottom: '8px' }}>
              {assets.length === 0 ? 'No assets in this chapter' : 'No enrolled students'}
            </p>
            <p style={{ fontSize: '14px', color: '#a0aec0' }}>
              {assets.length === 0 
                ? 'Add learning assets to this chapter to track progress' 
                : 'Enroll students in this course to see their progress'}
            </p>
          </div>
        ) : (
          <>
            {/* Progress Matrix Table */}
            <div style={{ 
              overflowX: 'auto', 
              marginTop: '20px',
              border: '1px solid #e2e8f0',
              borderRadius: '12px'
            }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'separate',
                borderSpacing: 0,
                minWidth: assets.length > 5 ? `${200 + (assets.length * 120)}px` : '100%'
              }}>
                <thead>
                  <tr style={{ background: '#f7fafc' }}>
                    {/* Student Name Column Header */}
                    <th style={{ 
                      padding: '16px',
                      textAlign: 'left',
                      fontSize: '13px',
                      fontWeight: '700',
                      color: '#2d3748',
                      borderBottom: '2px solid #e2e8f0',
                      position: 'sticky',
                      left: 0,
                      background: '#f7fafc',
                      zIndex: 10,
                      minWidth: '200px',
                      maxWidth: '250px'
                    }}>
                      Student Name
                    </th>
                    {/* Asset Column Headers */}
                    {assets.map((asset) => (
                      <th 
                        key={asset._id}
                        style={{ 
                          padding: '12px 8px',
                          textAlign: 'center',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#4a5568',
                          borderBottom: '2px solid #e2e8f0',
                          borderLeft: '1px solid #e2e8f0',
                          minWidth: '120px',
                          maxWidth: '200px',
                          verticalAlign: 'bottom'
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span style={{ fontSize: '20px' }}>
                            {getAssetTypeIcon(asset.assetType)}
                          </span>
                          <div style={{ 
                            wordBreak: 'break-word',
                            lineHeight: '1.3',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical'
                          }}>
                            {asset.title}
                          </div>
                          <span style={{ 
                            fontSize: '10px', 
                            color: '#a0aec0',
                            textTransform: 'uppercase'
                          }}>
                            {asset.assetType}
                          </span>
                        </div>
                      </th>
                    ))}
                    {/* Completion Percentage Column */}
                    <th style={{ 
                      padding: '16px',
                      textAlign: 'center',
                      fontSize: '13px',
                      fontWeight: '700',
                      color: '#2d3748',
                      borderBottom: '2px solid #e2e8f0',
                      borderLeft: '2px solid #cbd5e0',
                      minWidth: '100px',
                      background: '#f7fafc'
                    }}>
                      Progress
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {progressMatrix.map((student, studentIndex) => {
                    const completedCount = student.assetCompletion.filter(ac => ac.completed).length;
                    const studentProgress = assets.length > 0 
                      ? Math.round((completedCount / assets.length) * 100) 
                      : 0;

                    return (
                      <tr 
                        key={student.studentId}
                        style={{ 
                          background: studentIndex % 2 === 0 ? 'white' : '#fafafa'
                        }}
                      >
                        {/* Student Name Cell */}
                        <td style={{ 
                          padding: '16px',
                          borderBottom: '1px solid #e2e8f0',
                          position: 'sticky',
                          left: 0,
                          background: studentIndex % 2 === 0 ? 'white' : '#fafafa',
                          zIndex: 5,
                          minWidth: '200px',
                          maxWidth: '250px'
                        }}>
                          <div style={{ 
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px'
                          }}>
                            <div style={{ 
                              fontSize: '14px', 
                              fontWeight: '600',
                              color: '#2d3748'
                            }}>
                              {student.studentName}
                            </div>
                            <div style={{ 
                              fontSize: '12px', 
                              color: '#718096'
                            }}>
                              {student.studentEmail}
                            </div>
                          </div>
                        </td>
                        {/* Asset Completion Cells */}
                        {student.assetCompletion.map((completion) => (
                          <td 
                            key={completion.assetId}
                            style={{ 
                              padding: '12px',
                              textAlign: 'center',
                              borderBottom: '1px solid #e2e8f0',
                              borderLeft: '1px solid #e2e8f0',
                              background: completion.completed 
                                ? '#e6f9f0' 
                                : '#fef2f2'
                            }}
                          >
                            <div style={{
                              fontSize: '24px',
                              lineHeight: 1
                            }}>
                              {completion.completed ? '✓' : '✗'}
                            </div>
                          </td>
                        ))}
                        {/* Student Progress Cell */}
                        <td style={{ 
                          padding: '12px',
                          textAlign: 'center',
                          borderBottom: '1px solid #e2e8f0',
                          borderLeft: '2px solid #cbd5e0',
                          fontWeight: '600',
                          fontSize: '14px'
                        }}>
                          <div style={{
                            display: 'inline-block',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            background: studentProgress === 100 
                              ? '#c6f6d5' 
                              : studentProgress >= 50 
                                ? '#fef3c7' 
                                : '#fed7d7',
                            color: studentProgress === 100 
                              ? '#22543d' 
                              : studentProgress >= 50 
                                ? '#92400e' 
                                : '#742a2a'
                          }}>
                            {studentProgress}%
                          </div>
                          <div style={{
                            fontSize: '11px',
                            color: '#718096',
                            marginTop: '4px'
                          }}>
                            {completedCount}/{assets.length}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Legend */}
            <div style={{
              marginTop: '24px',
              padding: '16px',
              background: '#f7fafc',
              borderRadius: '8px',
              display: 'flex',
              gap: '24px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '14px', fontWeight: '600', color: '#4a5568' }}>
                Legend:
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: '#e6f9f0',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px'
                }}>
                  ✓
                </div>
                <span style={{ fontSize: '13px', color: '#4a5568' }}>Completed</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: '#fef2f2',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px'
                }}>
                  ✗
                </div>
                <span style={{ fontSize: '13px', color: '#4a5568' }}>Not Completed</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ChapterProgressMatrix;
